import { combineEpics, withState } from "redux-most";
import { allPass, chain, filter, flip, map, pipe } from "ramda";
import { of } from "most";
import { recoverWith } from "common/mostCurried";
import * as steps from "common/steps";
import * as fileDetailsActions from "./fileDetailsActions";
import * as applicationActions from "application/applicationActions";
import * as submissionActions from "submission/submissionActions";
import * as basicDetailsActions from "basic-details/basicDetailsActions";
import {
  getContributors,
  getStepFields,
  isAgeRangesEmpty,
  isContributorRolesEmpty,
  isLicenseTypesEmpty,
  isStepActive,
  isStepCompleted,
  isVideoRatingsEmpty,
  isVideoWorkType,
} from "common/selectors";
import {
  createAcknowledgeSubmissionEpic,
  createCompleteStepEpic,
  createLoadLocalStorageEpic,
  createSyncLocalStorageEpic,
} from "common/stepEpicCommon";
import { fetchContributorRoles, fetchVideoRatings } from "common/stipAjax";
import { fetchLicenseTypes } from "../common/apiAjax";
import { fetchAgeRanges } from "../common/stipAjax";

const isStepInitAction = (action) =>
  action.type === submissionActions.LOCAL_STORAGE_LOADED ||
  action.type === submissionActions.SET_ACTIVE_STEP;

const loadVideoRatingsFilter = ([state, action]) =>
  isStepInitAction(action) &&
  allPass([
    isStepActive(steps.FILE_DETAILS),
    isVideoWorkType,
    isVideoRatingsEmpty,
  ])(state);

export const loadVideoRatingsEpic = pipe(
  flip(withState),
  filter(loadVideoRatingsFilter),
  chain(
    pipe(
      fetchVideoRatings,
      map(fileDetailsActions.videoRatingsLoaded),
      recoverWith(pipe(applicationActions.appError, of))
    )
  )
);

const loadContributorsFilter = ([state, action]) =>
  isStepInitAction(action) && allPass([isContributorRolesEmpty])(state);

export const loadContributorRolesEpic = pipe(
  flip(withState),
  filter(loadContributorsFilter),
  chain(
    pipe(
      fetchContributorRoles,
      map(fileDetailsActions.contributorRolesLoaded),
      recoverWith(pipe(applicationActions.appError, of))
    )
  )
);

const loadAgeRangesFilter = ([state, action]) =>
  isStepInitAction(action) &&
  allPass([isStepActive(steps.FILE_DETAILS), isAgeRangesEmpty])(state);

export const loadAgeRangesEpic = pipe(
  flip(withState),
  filter(loadAgeRangesFilter),
  chain(
    pipe(
      fetchAgeRanges,
      map(fileDetailsActions.ageRangesLoaded),
      recoverWith(pipe(applicationActions.appError, of))
    )
  )
);

const loadLicenseTypesFilter = ([state, action]) =>
  isStepInitAction(action) &&
  allPass([isStepActive(steps.FILE_DETAILS), isLicenseTypesEmpty])(state);

export const loadLicenseTypesEpic = pipe(
  flip(withState),
  filter(loadLicenseTypesFilter),
  chain(
    pipe(
      fetchLicenseTypes,
      map(fileDetailsActions.licenseTypesLoaded),
      recoverWith(pipe(applicationActions.appError, of))
    )
  )
);

const syncLocalStorageEpic = createSyncLocalStorageEpic(
  [
    submissionActions.ADD_ANOTHER_WORK,
    submissionActions.CANCEL_SUBMISSION,
    submissionActions.SET_WORK_TYPE_ID,
    submissionActions.SUBMIT,
    fileDetailsActions.AUDIENCE_CHANGED,
    fileDetailsActions.COMPLETE_STEP,
    fileDetailsActions.FIELD_CHANGED,
    fileDetailsActions.PRIMARY_CATEGORY_CHANGED,
    fileDetailsActions.REMOVE_CONTRIBUTOR,
    fileDetailsActions.SAVE_ADD_CONTRIBUTOR,
    fileDetailsActions.SAVE_EDIT_CONTRIBUTOR,
    fileDetailsActions.SECONDARY_CATEGORY_CHANGED,
    fileDetailsActions.TERTIARY_CATEGORY_CHANGED,
    fileDetailsActions.VIDEO_RATING_CHANGED,
    basicDetailsActions.COMPLETE_STEP,
  ],
  {
    completed: isStepCompleted(steps.FILE_DETAILS),
    fields: getStepFields(steps.FILE_DETAILS),
    contributors: getContributors,
  },
  fileDetailsActions.localStorageSynced,
  steps.FILE_DETAILS
);

export default combineEpics([
  createAcknowledgeSubmissionEpic(steps.FILE_DETAILS),
  createCompleteStepEpic(fileDetailsActions.COMPLETE_STEP, steps.FILE_DETAILS),
  createLoadLocalStorageEpic(
    steps.FILE_DETAILS,
    fileDetailsActions.localStorageLoaded
  ),
  loadAgeRangesEpic,
  loadContributorRolesEpic,
  loadLicenseTypesEpic,
  loadVideoRatingsEpic,
  syncLocalStorageEpic,
]);
